import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import "./SalaryView.css";

const MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const MONTH_ABBR: Record<string,string> = {
  April:"Apr",May:"May",June:"Jun",July:"Jul",August:"Aug",September:"Sep",
  October:"Oct",November:"Nov",December:"Dec",January:"Jan",February:"Feb",March:"Mar",
};

interface SalaryConfig {
  raise_before_m3: number;
  raise_after_m3: number;
  bip_raise: number;
}
interface Employee {
  id?: number;
  employee_name: string;
  job_title: string;
  annual_salary: number;
  employment_type: string;
  bip_pct: number;
  hire_month: number;
}

interface BIPViewProps {
  costCenterNumber: string;
}

const fmt = (n: number) =>
  n === 0 ? "–" : "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0 });

const BIPView: React.FC<BIPViewProps> = ({ costCenterNumber }) => {
  const [config, setConfig] = useState<SalaryConfig>({ raise_before_m3: 0, raise_after_m3: 0.035, bip_raise: 0 });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [bipDrafts, setBipDrafts] = useState<Record<number, string>>({});
  const [forecastActive, setForecastActive] = useState(false);

  // Snapshot for dirty detection
  const savedSnapshot = useRef<string>("");
  const serializeEmployees = (emps: Employee[]) =>
    JSON.stringify(emps.map(e => ({ name: e.employee_name, bip: e.bip_pct, hm: e.hire_month })));
  const isDirty = !loading && serializeEmployees(employees) !== savedSnapshot.current;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, empRes] = await Promise.all([
        api.get("/salary/config"),
        api.get(`/salary/employees?cost_center=${costCenterNumber}`),
      ]);
      setConfig(cfgRes.data);
      const emps: Employee[] = empRes.data;
      setEmployees(emps);
      savedSnapshot.current = serializeEmployees(emps);
    } catch {
      setFeedback({ ok: false, msg: "Failed to load BIP data." });
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => { setForecastActive(!!res.data.active); })
      .catch(() => {});
  }, []);

  const updateBipPct = (idx: number, value: number) =>
    setEmployees(prev => prev.map((e, i) => i === idx ? { ...e, bip_pct: value } : e));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.post("/salary/employees", {
        cost_center: costCenterNumber,
        employees: employees.map(e => ({
          employee_name:   e.employee_name,
          job_title:       e.job_title,
          annual_salary:   e.annual_salary,
          employment_type: e.employment_type,
          bip_pct:         e.bip_pct,
          hire_month:      e.hire_month,
        })),
      });
      setFeedback({ ok: true, msg: "BIP saved successfully." });
      load();
    } catch {
      setFeedback({ ok: false, msg: "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  // Temp employees are not eligible for BIP or raises — exclude them
  const bipEligible = employees.filter(e => e.employment_type !== "Temp");
  const tempCount = employees.length - bipEligible.length;

  // BIP uses raise-adjusted salary as base (same raise schedule as salary)
  // Apr/May: salary × (1 + raise_before) × bip_pct × (1 + bip_raise) / 12
  // Jun–Mar: salary × (1 + raise_after)  × bip_pct × (1 + bip_raise) / 12
  const empWithMonthly = bipEligible.map(emp => {
    const monthly: Record<string, number> = {};
    MONTHS.forEach((m, i) => {
      const fiscalMonth = i + 1;
      const hm = emp.hire_month || 0;
      if (hm > 0 && fiscalMonth < hm) {
        monthly[m] = 0;
      } else {
        const raise = i < 2 ? config.raise_before_m3 : config.raise_after_m3;
        monthly[m] = Math.round(emp.annual_salary * (1 + raise) * emp.bip_pct * (1 + config.bip_raise) / 12);
      }
    });
    return { ...emp, monthly, annualBip: Object.values(monthly).reduce((s, v) => s + v, 0) };
  });

  const monthlyTotals: Record<string, number> = {};
  MONTHS.forEach(m => { monthlyTotals[m] = 0; });
  empWithMonthly.forEach(emp => { MONTHS.forEach(m => { monthlyTotals[m] += emp.monthly[m]; }); });
  const totalAnnualBip = MONTHS.reduce((s, m) => s + monthlyTotals[m], 0);

  const existing  = empWithMonthly.filter(e => !e.hire_month || e.hire_month === 0);
  const newHires  = empWithMonthly.filter(e => e.hire_month && e.hire_month > 0);

  const editingDisabled = !forecastActive;

  if (loading) return <div className="salary-loading">Loading BIP data…</div>;

  const bipTableBody = (rows: typeof empWithMonthly, isNewHire: boolean) =>
    rows.map((emp, idx) => {
      const realIdx = isNewHire
        ? existing.length + empWithMonthly.filter((e, i) => i < empWithMonthly.indexOf(emp) && e.hire_month && e.hire_month > 0).length
        : empWithMonthly.indexOf(emp);
      return (
        <tr key={idx} className="salary-row">
          <td className="col-name" style={{ fontWeight: 500 }}>
            {emp.employee_name || <span style={{ color: "oklch(0.55 0.02 250)" }}>—</span>}
            {isNewHire && emp.hire_month > 0 && (
              <span style={{ fontSize: 11, color: "oklch(0.4 0.1 70)", marginLeft: 6, background: "oklch(0.98 0.02 70)", padding: "1px 5px", borderRadius: 8 }}>
                from {MONTH_ABBR[MONTHS[emp.hire_month - 1]]}
              </span>
            )}
          </td>
          <td className="col-salary salary-calc-cell">
            {emp.annual_salary > 0 ? "$" + emp.annual_salary.toLocaleString() : "–"}
          </td>
          <td className="col-bip-pct" style={{ textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <input
                className="salary-input salary-input--num"
                type="number" min={0} max={100} step={0.5}
                value={realIdx in bipDrafts ? bipDrafts[realIdx] : (emp.bip_pct > 0 ? +(emp.bip_pct * 100).toFixed(1) : "")}
                placeholder="0"
                style={{ width: 72, textAlign: "center" }}
                disabled={editingDisabled}
                onFocus={() => setBipDrafts(prev => ({
                  ...prev,
                  [realIdx]: emp.bip_pct > 0 ? String(+(emp.bip_pct * 100).toFixed(1)) : "",
                }))}
                onChange={e => setBipDrafts(prev => ({ ...prev, [realIdx]: e.target.value }))}
                onBlur={e => {
                  const val = parseFloat(e.target.value) || 0;
                  updateBipPct(realIdx, val / 100);
                  setBipDrafts(prev => { const n = { ...prev }; delete n[realIdx]; return n; });
                }}
              />
              <span style={{ fontSize: 12, color: "oklch(0.5 0.02 250)" }}>%</span>
            </div>
          </td>
          {MONTHS.map(m => (
            <td key={m} className="col-month salary-calc-cell">
              {fmt(emp.monthly[m])}
            </td>
          ))}
          <td className="col-total salary-calc-cell salary-row-total">{fmt(emp.annualBip)}</td>
        </tr>
      );
    });

  return (
    <div className="salary-view bip-view">
      {editingDisabled && (
        <div className="salary-locked-banner">
          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 6 }}>lock</span>
          BIP editing is currently disabled. Contact the Finance Admin to open forecast planning.
        </div>
      )}
      {/* Sub-info banner */}
      <div className="salary-header">
        <div className="salary-header-sub-info">
          Account 60022 — Incentive Bonus
          {tempCount > 0 && (
            <span style={{ marginLeft: 10, color: "oklch(0.4 0.1 70)", background: "oklch(0.98 0.02 70)", border: "1px solid oklch(0.7 0.15 70)", padding: "1px 7px", borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
              {tempCount} Temp {tempCount === 1 ? "employee" : "employees"} excluded
            </span>
          )}
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="salary-empty-row" style={{ padding: 24, borderRadius: 10, border: "1.5px solid oklch(0.9 0.01 250)" }}>
          No employees found. Add employees on the <strong>Salary</strong> tab first.
        </div>
      ) : (
        <div className="salary-table-wrap">
          <table className="salary-table">
            <thead>
              <tr className="salary-thead-top">
                <th className="col-name">Name</th>
                <th className="col-salary" style={{ textAlign: "right" }}>Annual Salary</th>
                <th className="col-bip-pct">BIP %</th>
                {MONTHS.map(m => <th key={m} className="col-month">{MONTH_ABBR[m]}</th>)}
                <th className="col-total">Total FY</th>
              </tr>
            </thead>
            <tbody>
              {/* ── Existing Employees ── */}
              <tr className="salary-section-header-row">
                <td colSpan={15} className="salary-section-label">
                  <span className="salary-section-label-text">Existing Employees</span>
                </td>
              </tr>
              {existing.length === 0 ? (
                <tr>
                  <td colSpan={15} className="salary-empty-row">No existing employees with BIP.</td>
                </tr>
              ) : (
                bipTableBody(existing, false)
              )}

              {/* ── Spacer ── */}
              <tr className="salary-section-spacer"><td colSpan={15} /></tr>

              {/* ── Budget New Hires ── */}
              <tr className="salary-section-header-row salary-newhire-header">
                <td colSpan={15} className="salary-section-label">
                  <span className="salary-section-label-text">
                    <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>person_add</span>
                    Budget New Hires
                  </span>
                </td>
              </tr>
              {newHires.length === 0 ? (
                <tr>
                  <td colSpan={15} className="salary-empty-row">
                    No new hires on Salary tab.
                  </td>
                </tr>
              ) : (
                bipTableBody(newHires, true)
              )}
            </tbody>
            <tfoot>
              <tr className="salary-summary-row salary-total-row">
                <td colSpan={3} className="salary-summary-label">TOTAL BIP</td>
                {MONTHS.map(m => (
                  <td key={m} className="col-month salary-calc-cell">
                    {monthlyTotals[m] === 0 ? "–" : "$" + monthlyTotals[m].toLocaleString()}
                  </td>
                ))}
                <td className="col-total salary-calc-cell salary-row-total">
                  {totalAnnualBip === 0 ? "–" : "$" + totalAnnualBip.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Actions */}
      <div className="salary-actions">
        <div>
          {isDirty && !saving && (
            <span className="salary-unsaved-hint">
              <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>edit</span>
              You have unsaved changes
            </span>
          )}
        </div>
        <div className="salary-actions-right">
          {feedback && (
            <span className={`salary-feedback ${feedback.ok ? "salary-feedback--ok" : "salary-feedback--err"}`}>
              {feedback.msg}
            </span>
          )}
          <button
            className={`salary-save-btn${isDirty ? " salary-save-btn--active" : ""}`}
            onClick={handleSave}
            disabled={saving || !isDirty || employees.length === 0 || editingDisabled}
          >
            {saving ? "Saving…" : "Save BIP"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BIPView;
